import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { FaLock, FaUser, FaArrowRight } from 'react-icons/fa';

const LockScreen = ({ isOpen, onUnlock }) => {
    const [segundaClave, setSegundaClave] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const passwordRef = useRef(null);

    // Focus on mount or when opening
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => passwordRef.current?.focus(), 100);
            setSegundaClave('');
            setError('');
        }
    }, [isOpen]);

    const handleUnlock = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Only send 'segunda_clave'
            const response = await axios.post('/api/auth/verify-second-password', {
                segunda_clave: segundaClave
            });

            const { id_empleado, nombre_empleado, identificacion: newId } = response.data;

            // The token is set as an httpOnly cookie by the backend response.
            // We only update non-sensitive display cookies here.
            Cookies.set('id_empleado', id_empleado, { expires: 1 });
            Cookies.set('nombre_empleado', nombre_empleado, { expires: 1 });
            Cookies.set('identificacion', newId, { expires: 1 });

            // Remove any stale non-httpOnly 'token' cookie that might conflict
            Cookies.remove('token');

            // Clear lock state in localStorage
            localStorage.removeItem('appLocked');

            setLoading(false);
            onUnlock();
        } catch (err) {
            setLoading(false);
            setError(err.response?.data?.error || 'Error al validar credenciales');
            setSegundaClave('');
            passwordRef.current?.focus();
        }
    };

    const handleLogout = () => {
        Cookies.remove('token');
        Cookies.remove('id_empleado');
        Cookies.remove('nombre_empleado');
        Cookies.remove('identificacion');
        localStorage.removeItem('appLocked'); // Clear lock on logout too
        window.location.href = '/login';
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] bg-black-900/90 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700 animate-in fade-in zoom-in duration-300">
                {/* Header */}
                <div className="bg-yellow-500 p-6 flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                        <FaLock />
                        Sesión Bloqueada
                    </h2>
                </div>

                {/* Body */}
                <div className="p-8">
                    <p className="text-gray-600 dark:text-gray-400 mb-6 text-center">
                        Ingrese su <strong>Clave Rápida</strong> para desbloquear o cambiar de usuario.
                    </p>

                    <div className="flex justify-center mb-6">
                        <div className="bg-gray-100 p-4 rounded-full">
                            <FaUser className="text-gray-400 text-4xl" />
                        </div>
                    </div>

                    <form onSubmit={handleUnlock}>
                        <div className="mb-6">
                            <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                                Segunda Clave (Rápida)
                            </label>
                            <input
                                ref={passwordRef}
                                type="password"
                                value={segundaClave}
                                onChange={(e) => setSegundaClave(e.target.value)}
                                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                placeholder="******"
                                required
                            />
                        </div>

                        {error && (
                            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm text-center">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-black-500 hover:bg-gray-800 text-white font-bold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center gap-2"
                        >
                            {loading ? 'Validando...' : (
                                <>
                                    Desbloquear / Cambiar Usuario <FaArrowRight />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-6 text-center">
                        <button
                            onClick={handleLogout}
                            className="text-sm text-gray-500 hover:text-red-500 underline transition-colors"
                        >
                            Cerrar Sesión Completamente
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LockScreen;
